import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MoreHorizontal,
  Eye,
  Edit,
  Shield,
  ShieldOff,
  KeyRound,
  UserCog,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { BusyOverlay } from "@/components/common/BusyOverlay";
import { SuspendUserDialog } from "./SuspendUserDialog";
import { PermissionGate } from "@/components/common/PermissionGate";
import {
  useSuspendUser,
  useActivateUser,
  useResetPassword,
  useImpersonateUser,
} from "@/hooks/useUserQueries";
import {
  USER_COLUMNS,
  useUserColumnsStore,
  type UserColumnId,
} from "@/stores/userColumnsStore";
import type { User } from "@/types/admin";
import { formatDateTime } from "@/lib/utils";

// ============================================================================
// Types & Constants
// ============================================================================

/**
 * How wide each column is drawn.
 *
 * Kept here rather than in the store because a width is presentation, not preference — the store
 * holds only what gets written to localStorage. Typing it as a full `Record` over the id union is
 * the point: adding a column to `USER_COLUMNS` without giving it a width is a compile error, not a
 * column that silently collapses.
 *
 * The values are the px widths this table has always used (44/60/220/… = 1254px in total),
 * expressed on the spacing scale so they read like the rest of the codebase. A fixed-layout table
 * is widened to their sum, which is what makes the list overflow horizontally — no `min-w` is
 * needed to force it, and none is wanted: a hand-written one would have to be edited every time a
 * column is added and would be wrong the moment an operator hides one.
 */
const COLUMN_WIDTHS: Record<UserColumnId, string> = {
  select: "w-12",
  index: "w-16",
  // Stacks a full name over a full e-mail over a 36-character id, so it gets the room rather than
  // truncating all three.
  name: "w-88",
  phone: "w-40",
  role: "w-36",
  status: "w-28",
  security: "w-40",
  tenants: "w-24",
  lastLogin: "w-44",
  createdAt: "w-44",
  actions: "w-24",
};

interface UserTableProps {
  users: User[];
  isLoading?: boolean;
  /** Row number of the first user in `users`, e.g. (page - 1) * pageSize. */
  startIndex?: number;
  /** IDs currently ticked for export. Survives pagination, so it may name users off this page. */
  selectedIds: string[];
  /** Toggles one row. */
  onToggleSelect: (userId: string) => void;
  /** Ticks or unticks every row on the current page. */
  onToggleSelectPage: (select: boolean) => void;
}

// ============================================================================
// Component
// ============================================================================

export function UserTable({
  users,
  isLoading = false,
  startIndex = 0,
  selectedIds,
  onToggleSelect,
  onToggleSelectPage,
}: UserTableProps) {
  const navigate = useNavigate();

  // Which columns are on screen. Subscribed here, set from the toolbar's Columns menu — both talk
  // to the same store rather than threading props through the page.
  //
  // Subscribing to `hidden` and deriving the predicate locally, NOT subscribing to the store's own
  // `isVisible`: that one is a stable function reading the latest state, so a component that
  // subscribes to it re-renders for nothing that changed and never re-renders when a column is
  // toggled. `hidden` is the value that actually moves.
  const hiddenColumns = useUserColumnsStore((state) => state.hidden);
  const isVisible = (id: UserColumnId) => !hiddenColumns.includes(id);
  const visibleColumns = USER_COLUMNS.filter((column) => isVisible(column.id));

  // The label of the row write that currently covers the table, reported up by that row's menu.
  const [rowBusyLabel, setRowBusyLabel] = useState<string | null>(null);

  const selectedIdSet = new Set(selectedIds);
  const pageSelectedCount = users.filter((user) =>
    selectedIdSet.has(user.id),
  ).length;
  // Radix renders "indeterminate" as its own visual state, which is what a partly-ticked page
  // should show — a plain false would suggest nothing on this page is selected.
  const headerCheckedState =
    users.length > 0 && pageSelectedCount === users.length
      ? true
      : pageSelectedCount > 0
        ? "indeterminate"
        : false;

  if (isLoading) {
    // min-h-0 flex-1 for the same reason the empty state has it: CardContent is a flex column, so
    // a block sized to its own content leaves the card ending part-way down the screen and the
    // panel resizes the moment the rows arrive. Matches TenantTable / PaymentListTable.
    return (
      <div className="min-h-0 flex-1 space-y-3 overflow-hidden rounded-md border p-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <>
      {/*
        `[&>div]:overflow-visible` targets the wrapper `components/ui/table.tsx` puts around every
        table (`<div class="relative w-full overflow-auto">`), and it is what makes the horizontal
        scrollbar reachable. That wrapper is as tall as the whole table, so while IT owned the
        horizontal overflow its scrollbar rendered below the last row — off the bottom of the
        screen, where nobody could reach it. Switching it off hands both axes to this div, the one
        with a bounded height, so the bar sits at the bottom of the visible table.

        `scrollbar-thin` is the 6px bar used by every box that scrolls inside the page rather than
        being the page.
      */}
      <div className="scrollbar-thin min-h-0 flex-1 overflow-auto rounded-md border [&>div]:overflow-visible">
        {/* No `min-w`: a fixed-layout table is already widened to the sum of its <col> widths
            (1254px with every column on), so the overflow this scrollport exists for happens on its
            own — and a hand-written minimum would be wrong the moment a column is hidden. */}
        <Table className="table-fixed border-collapse [&_td:not(:last-child)]:border-r [&_th:not(:last-child)]:border-r">
          {/* Generated from USER_COLUMNS rather than written out, so a hidden column cannot leave
              its <col> behind — which would shift every width after it by one and is the one drift
              the browser gives no error for. */}
          <colgroup>
            {visibleColumns.map((column) => (
              <col key={column.id} className={COLUMN_WIDTHS[column.id]} />
            ))}
          </colgroup>
          <TableHeader>
            <TableRow>
              {isVisible("select") && (
                <TableHead className="text-center">
                  <Checkbox
                    checked={headerCheckedState}
                    onCheckedChange={(checked) =>
                      onToggleSelectPage(checked === true)
                    }
                    disabled={users.length === 0}
                    aria-label="Select all users on this page"
                    className="cursor-pointer align-middle"
                  />
                </TableHead>
              )}
              {isVisible("index") && (
                <TableHead className="text-center">STT</TableHead>
              )}
              {isVisible("name") && <TableHead>Name / Email</TableHead>}
              {isVisible("phone") && <TableHead>Phone Number</TableHead>}
              {isVisible("role") && <TableHead>Role</TableHead>}
              {isVisible("status") && <TableHead>Status</TableHead>}
              {isVisible("security") && <TableHead>Security</TableHead>}
              {isVisible("tenants") && (
                <TableHead className="text-center">Tenants</TableHead>
              )}
              {isVisible("lastLogin") && <TableHead>Last Login</TableHead>}
              {isVisible("createdAt") && <TableHead>Created</TableHead>}
              {isVisible("actions") && (
                <TableHead className="sticky right-0 z-20 bg-card text-center shadow-[inset_1px_0_0_0_var(--border)]">
                  Actions
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length}
                  className="h-24 text-center"
                >
                  No users found.
                </TableCell>
              </TableRow>
            ) : (
              users.map((user, index) => {
                const isActive = user.accountStatus === "Active";
                const roleColors: Record<string, string> = {
                  SuperAdmin: "bg-brand-soft text-brand-ink",
                  BillingAdmin: "bg-info-soft text-info-ink",
                  SupportAdmin: "bg-ok-soft text-ok-ink",
                };
                const roleColor =
                  roleColors[user.role] ?? "bg-muted text-muted-foreground";

                return (
                  <TableRow
                    key={user.id}
                    className="cursor-pointer hover:bg-muted/50"
                  >
                    {/* Export selection */}
                    {isVisible("select") && (
                      <TableCell className="text-center">
                        <Checkbox
                          checked={selectedIdSet.has(user.id)}
                          onCheckedChange={() => onToggleSelect(user.id)}
                          aria-label={`Select ${user.fullName}`}
                          className="cursor-pointer align-middle"
                        />
                      </TableCell>
                    )}

                    {/* STT */}
                    {isVisible("index") && (
                      <TableCell className="text-center text-muted-foreground">
                        {startIndex + index + 1}
                      </TableCell>
                    )}

                    {/* Name / Email */}
                    {isVisible("name") && (
                      <TableCell>
                        <div className="min-w-0 space-y-0.5">
                          <button
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            className="block w-full truncate text-left font-medium text-primary hover:underline"
                            title={user.fullName}
                          >
                            {user.fullName}
                          </button>
                          <p
                            className="truncate text-xs text-muted-foreground"
                            title={user.email}
                          >
                            {user.email}
                          </p>
                          {/* The id the search box now accepts. Without it on screen the operator
                              has no way to obtain the value the box asks them to paste - the same
                              reason the tenant list prints its id under the workspace name. */}
                          <p
                            className="truncate font-mono text-[11px] text-muted-foreground"
                            title={user.id}
                          >
                            {user.id}
                          </p>
                        </div>
                      </TableCell>
                    )}

                    {/* Phone Number */}
                    {isVisible("phone") && (
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {user.phoneNumber || "—"}
                      </TableCell>
                    )}

                    {/* Role */}
                    {isVisible("role") && (
                      <TableCell>
                        <Badge className={roleColor}>{user.role}</Badge>
                      </TableCell>
                    )}

                    {/* Status */}
                    {isVisible("status") && (
                      <TableCell>
                        <Badge variant={isActive ? "default" : "destructive"}>
                          {user.accountStatus}
                        </Badge>
                      </TableCell>
                    )}

                    {/* Security: Email Verified + 2FA */}
                    {isVisible("security") && (
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1 text-xs">
                            {user.emailVerified ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                            ) : (
                              <XCircle className="h-3.5 w-3.5 text-muted-foreground" />
                            )}
                            <span className="text-muted-foreground">Email</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs">
                            {user.twoFactorEnabled ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                            ) : (
                              <XCircle className="h-3.5 w-3.5 text-muted-foreground" />
                            )}
                            <span className="text-muted-foreground">2FA</span>
                          </div>
                        </div>
                      </TableCell>
                    )}

                    {/* Tenants */}
                    {isVisible("tenants") && (
                      <TableCell className="text-center">
                        <Badge variant="outline">{user.tenantCount}</Badge>
                      </TableCell>
                    )}

                    {/* Last Login */}
                    {isVisible("lastLogin") && (
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {user.lastLoginAt
                          ? formatDateTime(user.lastLoginAt)
                          : "Never"}
                      </TableCell>
                    )}

                    {/* Created */}
                    {isVisible("createdAt") && (
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {formatDateTime(user.createdAt)}
                      </TableCell>
                    )}

                    {/* Actions */}
                    {isVisible("actions") && (
                      <TableCell className="sticky right-0 z-10 bg-card">
                        {/* Centred and pinned: the column stays reachable once the table is wider
                            than the viewport, and the trigger sits under its own header rather
                            than drifting to the cell's right edge. */}
                        <div className="flex items-center justify-center">
                          <UserRowActionsMenu user={user} onBusyChange={setRowBusyLabel} />
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Covers the list's Card (the page's `relative` Card is the nearest positioned ancestor), not
          the page: a suspension, activation or impersonation started from a row must not let the
          table take a second write, while the rest of the console stays usable. */}
      <BusyOverlay contained open={rowBusyLabel !== null} label={rowBusyLabel ?? ""} />
    </>
  );
}

/**
 * Row actions for the user table.
 *
 * Each row mounts its own instances of the four write mutations rather than sharing one set with
 * the table, which is what makes `isPending` mean "THIS row's write" — one shared instance would
 * have shown every row's trigger spinning together for a request only one of them made, and
 * would have let a click on row B's Suspend fire while row A's was still settling.
 */
function UserRowActionsMenu({
  user,
  onBusyChange,
}: {
  user: User;
  /**
   * The table's busy-label setter. This row sets its label while its write runs, and clears it only
   * if its own label is still the one showing — never another row's.
   */
  onBusyChange: Dispatch<SetStateAction<string | null>>;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    kind: "resetPassword" | "impersonate" | null;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    open: false,
    kind: null,
    title: "",
    description: "",
    onConfirm: () => {},
  });

  const suspendUser = useSuspendUser();
  const activateUser = useActivateUser();
  const resetPassword = useResetPassword();
  const impersonateUser = useImpersonateUser();
  // Suspend has a dialog of its own, because it asks for the reason.
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  // Same reason as UserDetailPage: impersonation redirects with `window.location.href`, which
  // does not resolve the mutation, so `isPending` alone would drop the busy state a moment before
  // the browser actually leaves.
  const [isLeavingForImpersonation, setIsLeavingForImpersonation] = useState(false);
  const impersonationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Same two failure modes as UserDetailPage: bfcache restoring this exact page (Back from the
  // client portal) and anything else that keeps the response from ever reaching
  // `window.location.href`. Either would otherwise leave this row's overlay up forever.
  useEffect(() => {
    if (!isLeavingForImpersonation) {
      return;
    }

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // A bfcache restore here means the impersonation already SUCCEEDED and the operator hit
        // Back — the client portal opened, this admin tab was merely parked. Claiming "the
        // redirect did not happen" is not just wrong, it invites a second impersonation, which
        // means another session and another audit row. Reset silently.
        setIsLeavingForImpersonation(false);
      }
    };
    window.addEventListener("pageshow", handlePageShow);

    // 20s, not 10s: a slow cross-origin portal load must not re-enable Impersonate mid-navigation
    // while the tab is still on its way out. Past this cap something genuinely did not go
    // through, so it is safe to invite a retry — but that is a real "nothing opened" case, not a
    // failure, so the message stays neutral rather than asserting a redirect that may still land.
    impersonationTimeoutRef.current = setTimeout(() => {
      setIsLeavingForImpersonation(false);
      toast.info(
        "Still waiting for the client portal — you can try again if nothing opened.",
      );
    }, 20_000);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      if (impersonationTimeoutRef.current) {
        clearTimeout(impersonationTimeoutRef.current);
        impersonationTimeoutRef.current = null;
      }
    };
  }, [isLeavingForImpersonation]);

  const isImpersonatePending = impersonateUser.isPending || isLeavingForImpersonation;
  // Disables the rest of THIS row's write items while any one of them is out.
  const anyPending =
    suspendUser.isPending ||
    activateUser.isPending ||
    resetPassword.isPending ||
    isImpersonatePending;
  // The table overlay is reserved for writes that must not interleave with anything else —
  // status/suspension and impersonate, matching UserDetailPage's own `anyWritePending`. Reset
  // password's own ConfirmDialog already holds open with a spinner for that wait, so it stays out.
  //
  // Derived from whichever mutation is actually pending, not from `confirmDialog.kind` — that field
  // keeps its last value after the dialog closes, so a Suspend followed later by an Activate (which
  // opens no dialog at all) would otherwise keep reading "Suspending…".
  const busyLabel = activateUser.isPending
    ? `Activating ${user.fullName}…`
    : isImpersonatePending
      ? `Starting impersonation as ${user.fullName}…`
      : suspendUser.isPending
        ? `Suspending ${user.fullName}…`
        : null;

  // Reported up rather than rendered here: this menu lives in the sticky actions cell, which is a
  // positioned box, so an overlay rendered in place would cover the cell instead of the table.
  // UserTable draws the one overlay over the list's Card.
  //
  // Set only while this row has a label, and cleared only if that label is still the one showing: a
  // row that mounts, unmounts or re-renders while another row's write is running — the list re-reads
  // before that write settles — must not lift the other row's overlay.
  useEffect(() => {
    if (!busyLabel) return;
    onBusyChange(busyLabel);
    return () => onBusyChange((current) => (current === busyLabel ? null : current));
  }, [busyLabel, onBusyChange]);

  const confirmIsPending =
    confirmDialog.kind === "resetPassword"
      ? resetPassword.isPending
      : confirmDialog.kind === "impersonate"
        ? isImpersonatePending
        : false;

  const isActive = user.accountStatus === "Active";

  const handleSuspend = () => {
    setSuspendDialogOpen(true);
  };

  const handleConfirmSuspend = (reason: string) => {
    suspendUser.mutate(
      { id: user.id, reason },
      {
        // Closed on success only: on failure the dialog stays open with the reason kept, so a
        // retry does not mean typing it again.
        onSuccess: () => setSuspendDialogOpen(false),
      },
    );
  };

  const handleResetPassword = () => {
    setConfirmDialog({
      open: true,
      kind: "resetPassword",
      title: "Reset Password",
      description: `Send password reset email to ${user.email}?`,
      onConfirm: () => {
        // Same reason as suspend: close on settle, not just on success.
        resetPassword.mutate(user.id, {
          onSettled: () => setConfirmDialog((prev) => ({ ...prev, open: false })),
        });
      },
    });
  };

  const handleImpersonate = () => {
    setConfirmDialog({
      open: true,
      kind: "impersonate",
      title: "Impersonate User",
      description: `You will be logged in as ${user.fullName}. Continue?`,
      onConfirm: () => {
        impersonateUser.mutate(
          { id: user.id, reason: "Impersonation from admin panel" },
          {
            onSuccess: () => {
              setIsLeavingForImpersonation(true);
              setConfirmDialog((prev) => ({ ...prev, open: false }));
            },
            // No navigation is coming on error, so nothing keeps the dialog open on purpose —
            // close it here rather than onSettled, so success doesn't also run this branch.
            onError: () => setConfirmDialog((prev) => ({ ...prev, open: false })),
          },
        );
      },
    });
  };

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          {/* Spins while this row has a write out. The menu itself closes on select: a Radix menu is
              modal, so holding it open for the request locked the whole console. */}
          <Button variant="ghost" size="sm" aria-busy={anyPending}>
            {anyPending ? <Spinner /> : <MoreHorizontal className="h-4 w-4" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate(`/admin/users/${user.id}`)}>
            <Eye className="mr-2 h-4 w-4" />
            View Details
          </DropdownMenuItem>
          {/* Everything below writes to the user, and every one of those endpoints
            is SuperAdmin-only - a SupportAdmin still gets "View Details". While any one of
            them is out, the rest of this row's write items are disabled too. */}
          <PermissionGate roles="SuperAdmin">
            <DropdownMenuItem
              disabled={anyPending}
              onClick={() => navigate(`/admin/users/${user.id}/edit`)}
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit User
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {isActive ? (
              <DropdownMenuItem disabled={anyPending} onClick={handleSuspend}>
                <ShieldOff className="mr-2 h-4 w-4" />
                Suspend
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                disabled={anyPending}
                // Closes on select: the table overlay names the write and the trigger spins until it
                // settles.
                onClick={() => activateUser.mutate({ id: user.id, notes: "" })}
              >
                {activateUser.isPending ? (
                  <Spinner className="mr-2 h-4 w-4" />
                ) : (
                  <Shield className="mr-2 h-4 w-4" />
                )}
                Activate
              </DropdownMenuItem>
            )}
            <DropdownMenuItem disabled={anyPending} onClick={handleResetPassword}>
              <KeyRound className="mr-2 h-4 w-4" />
              Reset Password
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled={anyPending} onClick={handleImpersonate}>
              <UserCog className="mr-2 h-4 w-4" />
              Impersonate
            </DropdownMenuItem>
          </PermissionGate>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => {
          if (open || !confirmIsPending) {
            setConfirmDialog((prev) => ({ ...prev, open }));
          }
        }}
        title={confirmDialog.title}
        description={confirmDialog.description}
        onConfirm={confirmDialog.onConfirm}
        isPending={confirmIsPending}
      />

      <SuspendUserDialog
        open={suspendDialogOpen}
        onOpenChange={setSuspendDialogOpen}
        userName={user.fullName}
        isPending={suspendUser.isPending}
        onConfirm={handleConfirmSuspend}
      />

    </>
  );
}
